/* eslint-disable import/no-unused-modules */
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import type {
    Currency,
    InvoicePaymentStatus,
    Proposal,
} from '../../type/types';
import {
    proposalInvoicePaymentColumnsClass,
    proposalQuotePaymentColumnsClass,
} from '../../constants';
import { Button, cn } from '@rocco/index';
import { getCountryByCurrency, proposalFormatPrice } from '../../util/utils';
import { CheckLine } from '@archipro-design/icons';
import type { PaymentItem } from '../../type/types';

dayjs.extend(timezone);
dayjs.extend(utc);

interface PaymentRowProps {
    proposal: Proposal;
    paymentItem: PaymentItem;
    currency: Currency;
    isMobile: boolean;
    onPayNowClick: (paymentId: number) => void;
    hasPayButton: boolean;
}

const TYPO_CLASS = {
    NAME: 'font-medium md:text-[0.76rem] text-[#141414]',
    BADGE_LIGHT_TEXT:
        'font-medium text-[1.09rem] md:text-[0.79rem] text-[#141414] md:text-black',
    BADGE_DARK_TEXT: 'font-medium text-[1.09rem] text-white',
    DATE: 'font-display font-medium md:font-[450] md:text-[0.86rem] tracking-[-0.01em] md:tracking-normal text-[#9B9B9B] md:text-[#7E7E7E]',
    // weird bug, removed font-display looks closer to figma
    PRICE: 'font-semibold md:font-medium md:text-[0.86rem] tracking-[-0.01em] text-[#141414]',
    // for mobile only
    COLUMN_HEADER:
        'md:font-sans font-medium md:font-normal text-[0.54rem] md:text-sm md:leading-normal tracking-[-0.01em] md:tracking-normal text-[#9B9B9B]',
};

/**
 * Formats a timestamp into a localised date string using dayjs with local timezone.
 * non-US: DD.MM.YYYY (e.g. 17.02.2026)
 * US: MM.DD.YYYY (e.g. 02.17.2026)
 * @param timestamp - Unix timestamp (seconds or milliseconds)
 * @param country - Country code to determine date format
 * @returns Formatted date string, or "-" if timestamp is null/undefined
 */
const formatDate = (
    timestamp: number | null,
    country: 'NZ' | 'AU' | 'US'
): string => {
    if (!timestamp) {
        return '-';
    }

    if (country === 'US') {
        return dayjs(timestamp).format('MM.DD.YYYY');
    }

    return dayjs(timestamp).format('DD.MM.YYYY');
};

const getStatusName = (status: InvoicePaymentStatus) => {
    let statusName = '';
    switch (status) {
        case 'Upcoming':
            statusName = 'Unpaid';
            break;
        default:
            statusName = status;
    }
    // capitalized
    return statusName.charAt(0).toUpperCase() + statusName.slice(1);
};

const getQuoteStatusBadge = (status: InvoicePaymentStatus) => {
    return (
        <Button
            size="narrow"
            textSize="small"
            className="border-none bg-[#E7E7E7]"
        >
            <span className={cn(TYPO_CLASS.BADGE_LIGHT_TEXT)}>
                {getStatusName(status)}
            </span>
        </Button>
    );
};

const InvoiceStatusBadge = ({
    hasPayButton,
    status,
    onClick,
}: {
    status: PaymentItem['status'];
    hasPayButton: boolean;
    onClick: () => void;
}) => {
    if (status === 'FullyPaid') {
        return (
            <Button className="h-11 w-24 border-none bg-[#D0D0D0]">
                <span
                    className={cn(
                        TYPO_CLASS.BADGE_DARK_TEXT,
                        'ml-1 mt-[0.2rem]'
                    )}
                >
                    Paid
                    <CheckLine className="mb-1 ml-2 h-6 w-6 fill-white" />
                </span>
            </Button>
        );
    }
    return (
        <Button
            color="primary-bm"
            className="h-11 w-24 border-none"
            onClick={onClick}
            disabled={!hasPayButton}
        >
            <span className={TYPO_CLASS.BADGE_LIGHT_TEXT}>Pay</span>
        </Button>
    );
};

/**
 * Payment row component for displaying payment information in a grid layout
 */
export const PaymentRow = ({
    proposal,
    paymentItem,
    currency,
    isMobile,
    onPayNowClick,
    hasPayButton,
}: PaymentRowProps) => {
    const docType = proposal.docType;
    const country = getCountryByCurrency(currency);
    if (isMobile) {
        return (
            <div className="rounded-md bg-[#f6f6f6] p-3">
                {/* First row: Payment name */}
                <div className={cn(TYPO_CLASS.NAME, 'mb-3')}>
                    {paymentItem.name}
                </div>

                {/* Second row: Status badge, due date, and price */}
                <div className="flex items-center gap-2">
                    {/* Status badge - flexible width */}
                    <div className="w-[36%] flex-shrink-0">
                        {docType === 'quote' ? (
                            getQuoteStatusBadge(paymentItem.status)
                        ) : (
                            <InvoiceStatusBadge
                                status={paymentItem.status}
                                hasPayButton={hasPayButton}
                                onClick={() => {
                                    onPayNowClick(paymentItem.id);
                                }}
                            />
                        )}
                    </div>

                    {/* Due date - flexible width */}
                    <div className="flex w-[32%] flex-col">
                        <span
                            className={cn(
                                TYPO_CLASS.COLUMN_HEADER,
                                'mb-1 uppercase'
                            )}
                        >
                            Due
                        </span>
                        <span className={TYPO_CLASS.DATE}>
                            {formatDate(paymentItem.dueDate, country)}
                        </span>
                    </div>

                    {/* Price - flexible width */}
                    <div className="flex w-[32%] flex-col text-right">
                        <span
                            className={cn(
                                TYPO_CLASS.COLUMN_HEADER,
                                'mb-1 uppercase'
                            )}
                        >
                            Price
                        </span>
                        <span className={TYPO_CLASS.PRICE}>
                            {proposalFormatPrice(paymentItem.amount, currency)}
                        </span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`grid ${docType === 'quote' ? proposalQuotePaymentColumnsClass : proposalInvoicePaymentColumnsClass} min-h-14 items-center rounded-md bg-[#f6f6f6] backdrop-blur-[30px]`}
        >
            <div className="ml-6 text-[0.76rem] font-medium text-[#141414]">
                {paymentItem.name}
            </div>
            {docType === 'quote' && (
                <>
                    <div className="flex justify-center">
                        {getQuoteStatusBadge(paymentItem.status)}
                    </div>
                    <div className={cn(TYPO_CLASS.DATE, 'print:text-base')}>
                        {formatDate(paymentItem.dueDate, country)}
                    </div>
                    <div className={cn(TYPO_CLASS.DATE, 'print:text-base')}>
                        {formatDate(paymentItem.paymentDate, country)}
                    </div>
                    <div
                        className={cn(
                            TYPO_CLASS.PRICE,
                            'mr-6 text-right print:text-base'
                        )}
                    >
                        {proposalFormatPrice(paymentItem.amount, currency)}
                    </div>
                </>
            )}
            {docType === 'invoice' && (
                <>
                    <div className={cn(TYPO_CLASS.DATE, 'print:text-base')}>
                        {formatDate(paymentItem.dueDate, country)}
                    </div>
                    <div className={cn(TYPO_CLASS.DATE, 'print:text-base')}>
                        {formatDate(paymentItem.paymentDate, country)}
                    </div>
                    <div
                        className={cn(
                            TYPO_CLASS.PRICE,
                            'mr-6 text-right print:text-base'
                        )}
                    >
                        {proposalFormatPrice(paymentItem.amount, currency)}
                    </div>
                    <div className="flex justify-center">
                        <InvoiceStatusBadge
                            status={paymentItem.status}
                            hasPayButton={hasPayButton}
                            onClick={() => {
                                onPayNowClick(paymentItem.id);
                            }}
                        />
                    </div>
                </>
            )}
        </div>
    );
};

export default PaymentRow;
